import React from 'react';
import { Box, Container, Typography, Grid, Card } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LoginIcon from '@mui/icons-material/Login';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GavelIcon from '@mui/icons-material/Gavel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';

const faqs = [
  {
    question: 'What is the X Funding Challenge?',
    answer: 'X Funding Challenge is a performance-based trading evaluation designed to assess a trader\'s consistency, discipline, and strategy under specific risk and rule parameters. It helps traders benchmark their skills in a controlled environment.',
    icon: EmojiEventsIcon,
    color: '#fbbf24' // Gold
  },
  {
    question: 'How does the Challenge work?',
    answer: 'You can choose from three challenge tiers — 25K, 50K, or 100K — depending on your comfort level. Once enrolled, you\'ll receive access credentials and can begin your evaluation. The challenge focuses on consistency, drawdown control, and responsible trading behavior.',
    icon: TrendingUpIcon,
    color: '#10b981' // Green
  },
  {
    question: 'How do I join a Challenge?',
    answer: 'Select your preferred plan from the Challenge Plans Page, complete the registration form, and make the one-time entry fee payment. You\'ll receive login credentials and challenge details within 24 hours of verification.',
    icon: LoginIcon,
    color: '#3b82f6' // Blue
  },
  {
    question: 'What is the Reward Request system?',
    answer: 'After successfully completing your evaluation phase, you can request a reward review via the Reward Request Form on the website. You\'ll be asked to enter your registered email or login ID, Challenge type (25K/50K/100K), and Reward size. Once submitted, your data will be internally verified within 24 hours by our compliance and audit team.',
    icon: CardGiftcardIcon,
    color: '#ec4899' // Pink
  },
  {
    question: 'How are rewards processed?',
    answer: 'Rewards are only processed after verification of your evaluation performance and rule compliance. The process is internal, and all reward exits are final once approved by our review team. This system ensures fairness and transparency.',
    icon: VerifiedUserIcon,
    color: '#8b5cf6' // Purple
  },
  {
    question: 'What are the rules I should follow during the Challenge?',
    answer: 'All participants are required to adhere to fair usage and compliance policies: No toxic trading flow, No hedging across accounts, No latency or tick manipulation, and Minimum trade time and activity rules. Violations can lead to disqualification. Full details are listed on our Terms of Use page.',
    icon: GavelIcon,
    color: '#ef4444' // Red
  },
  {
    question: 'How long does it take to receive credentials after registration?',
    answer: 'Once your registration and payment are verified, credentials are typically shared within 24 hours to your registered email address.',
    icon: AccessTimeIcon,
    color: '#06b6d4' // Cyan
  },
  {
    question: 'Can I participate in multiple challenges?',
    answer: 'Yes. You can register for more than one challenge at a time, as long as each registration uses a unique account and complies with our trading policies.',
    icon: LibraryAddIcon,
    color: '#14b8a6' // Teal
  },
  {
    question: 'What if I lose access to my account?',
    answer: 'You can recover your credentials using the "Forgot Password" option or contact support with your registered email for assistance.',
    icon: LockOpenIcon,
    color: '#f59e0b' // Orange
  },
  {
    question: 'Is X Funding a broker or investment firm?',
    answer: 'No. X Funding does not offer brokerage, investment, or trading services. The challenge is a performance evaluation program designed purely for educational and assessment purposes.',
    icon: BusinessCenterIcon,
    color: '#6366f1' // Indigo
  }
];

function FAQ() {
  return (
    <Box sx={{ bgcolor: '#131722', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="body1" sx={{ color: '#d1d4dc', fontSize: '1.1rem' }}>
            Everything you need to know about the X Funding Challenge
          </Typography>
        </Box>

        {/* FAQ Grid */}
        <Grid container spacing={3}>
          {faqs.map((faq, idx) => {
            const IconComponent = faq.icon;
            return (
              <Grid item xs={12} md={6} key={idx}>
                <Card
                  sx={{
                    bgcolor: '#1e222d',
                    border: '1px solid #2a2e39',
                    borderRadius: 3,
                    p: 3,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${faq.color}40`,
                      borderColor: faq.color,
                      '& .icon-wrapper': {
                        transform: 'scale(1.1) rotate(5deg)',
                        boxShadow: `0 4px 16px ${faq.color}60`
                      }
                    }
                  }}
                >
                  {/* Icon */}
                  <Box
                    className="icon-wrapper"
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${faq.color}20, ${faq.color}40)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                      transition: 'all 0.3s ease',
                      border: `2px solid ${faq.color}60`
                    }}
                  >
                    <IconComponent sx={{ color: faq.color, fontSize: 32 }} />
                  </Box>

                  {/* Question */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#fff',
                      mb: 2,
                      fontSize: '1.15rem'
                    }}
                  >
                    {faq.question}
                  </Typography>

                  {/* Answer */}
                  <Typography
                    sx={{
                      color: '#d1d4dc',
                      lineHeight: 1.7,
                      fontSize: '0.95rem'
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}

export default FAQ;
